import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface AddressInfoCondensedDisplayStyles {
    wrapper?: InjectableCss;
    addressText?: TypographyProps;
    cityStatePostalCodeText?: TypographyProps;
    countryText?: TypographyProps;
}

export const addressInfoCondensedDisplayStyles: AddressInfoCondensedDisplayStyles = {
    wrapper: {
        css: css`
            font-style: normal;
        `,
    },
    addressText: { as: "p" },
    cityStatePostalCodeText: { as: "p" },
    countryText: { as: "p" },
};

interface OwnProps {
    address1: string;
    address2?: string;
    address3?: string;
    address4?: string;
    city: string;
    state?: string;
    postalCode: string;
    country?: string;
    extendedStyles?: AddressInfoCondensedDisplayStyles;
}

type Props = OwnProps;

const StyledAddress = getStyledWrapper("address");

const AddressInfoCondensedDisplay: React.FunctionComponent<Props> = function Address(props: Props) {
    const { extendedStyles, address1, address2, address3, address4, city, state, postalCode, country } = props;
    const [styles] = React.useState(() => mergeToNew(addressInfoCondensedDisplayStyles, extendedStyles));
    return (
        <StyledAddress {...styles.wrapper}>
            <AddressLine
                address1={address1}
                address2={address2}
                address3={address3}
                address4={address4}
                styles={styles}
            />
            <CityStatePostalCode city={city} state={state} postalCode={postalCode} styles={styles} />
            {country && <Typography {...styles.countryText}>{country}</Typography>}
        </StyledAddress>
    );
};

interface CityStatePostalCodeProps {
    city: string;
    state?: string;
    postalCode: string;
    styles: AddressInfoCondensedDisplayStyles;
}

const CityStatePostalCode: React.FunctionComponent<CityStatePostalCodeProps> = function CityStatePostalCode(
    props: CityStatePostalCodeProps,
) {
    const { city, state, postalCode, styles } = props;
    let cityStatePostalDisplay = city;
    if (state) {
        cityStatePostalDisplay += `, ${state}`;
    }
    if (postalCode) {
        cityStatePostalDisplay += ` ${postalCode}`;
    }
    return <Typography {...styles.cityStatePostalCodeText}>{cityStatePostalDisplay}</Typography>;
};

interface AddressLineProps {
    address1: string;
    address2?: string;
    address3?: string;
    address4?: string;
    styles: AddressInfoCondensedDisplayStyles;
}

const AddressLine: React.FunctionComponent<AddressLineProps> = (props: AddressLineProps) => {
    const { address1, address2, address3, address4, styles } = props;
    let addressLine = address1;
    if (address2) {
        addressLine += `, ${address2}`;
    }
    if (address3) {
        addressLine += `, ${address3}`;
    }
    if (address4) {
        addressLine += `, ${address4}`;
    }
    return <Typography {...styles.addressText}>{addressLine}</Typography>;
};

export default AddressInfoCondensedDisplay;
